import React, { cloneElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDownShortWide,
  faArrowDownWideShort,
  faChevronLeft,
  faChevronRight,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import Dropdown from "./Dropdown";
import InputField from "./InputField";

const defaultRowsPerPageOptions = [10, 30, 50, 70, 100];
const defaultHeaderClassName = "min-w-[120px] max-w-[240px] text-left align-top";
const defaultCellClassName = "min-w-[120px] max-w-[240px] align-top whitespace-normal break-words";

const getWidthClassName = (column, fallbackClassName) =>
  column.widthClassName ?? fallbackClassName;

const getWidthStyle = (column) =>
  column.width ? { width: column.width } : undefined;

const compareSortValues = (aValue, bValue, sortOrder) => {
  if (aValue == null && bValue == null) {
    return 0;
  }

  if (aValue == null) {
    return sortOrder === "asc" ? -1 : 1;
  }

  if (bValue == null) {
    return sortOrder === "asc" ? 1 : -1;
  }

  if (typeof aValue === "number" && typeof bValue === "number") {
    if (aValue === bValue) {
      return 0;
    }

    return sortOrder === "asc" ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
  }

  if (aValue instanceof Date && bValue instanceof Date) {
    const diff = aValue.getTime() - bValue.getTime();

    if (diff === 0) {
      return 0;
    }

    return sortOrder === "asc" ? (diff > 0 ? 1 : -1) : (diff < 0 ? 1 : -1);
  }

  const normalizedA = aValue.toString().toLowerCase();
  const normalizedB = bValue.toString().toLowerCase();

  if (normalizedA === normalizedB) {
    return 0;
  }

  return sortOrder === "asc"
    ? normalizedA > normalizedB
      ? 1
      : -1
    : normalizedA < normalizedB
      ? 1
      : -1;
};

const DataTable = ({
  rows = [],
  columns = [],
  search = "",
  defaultSortColumn = "",
  defaultSortOrder = "asc",
  defaultRowsPerPage = 30,
  rowsPerPageOptions = defaultRowsPerPageOptions,
  emptyColSpan,
  rowKey,
  rowClassName,
  tableClassName = "table-default w-full",
  tableMinWidthClassName = "",
  containerClassName = "w-full min-w-0 overflow-x-auto pb-[250px] mb-[-250px]",
  emptyContent = "",
  onSearchChange,
  searchPlaceholder,
  externalPagination = false,
  externalPage = 1,
  externalRowsPerPage = 10,
  externalTotalRows = 0,
  externalTotalPages = 1,
  onExternalPageChange,
  onExternalRowsPerPageChange,
  showPagination = true,
  headerRowClassName = "",
  skipClientSearch = false,
  defaultHeaderClassName: headerClassName = defaultHeaderClassName,
  defaultCellClassName: cellClassName = defaultCellClassName,
}) => {
  const { t } = useTranslation();
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const visibleColumns = useMemo(
    () => columns.filter((column) => column.hidden !== true),
    [columns]
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.toLowerCase();

    let filtered = rows;

    if (normalizedSearch && !skipClientSearch) {
      filtered = rows.filter((row) =>
        visibleColumns.some((column) => {
          if (column.searchable === false) {
            return false;
          }

          const value = column.getSortValue
            ? column.getSortValue(row)
            : column.render(row);

          return value?.toString().toLowerCase().includes(normalizedSearch);
        })
      );
    }

    if (!sortColumn) {
      return filtered;
    }

    const activeColumn = visibleColumns.find((column) => column.key === sortColumn);

    if (!activeColumn) {
      return filtered;
    }

    return [...filtered].sort((a, b) => {
      const aValue = activeColumn.getSortValue
        ? activeColumn.getSortValue(a)
        : activeColumn.render(a);
      const bValue = activeColumn.getSortValue
        ? activeColumn.getSortValue(b)
        : activeColumn.render(b);

      return compareSortValues(aValue, bValue, sortOrder);
    });
  }, [rows, search, skipClientSearch, sortColumn, sortOrder, visibleColumns]);

  useEffect(() => {
    if (!externalPagination) {
      setPage(1);
    }
  }, [externalPagination, search]);

  useEffect(() => {
    if (externalPagination) {
      return;
    }

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));

    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [externalPagination, filteredRows.length, page, rowsPerPage]);

  const handleSort = (column) => {
    if (!column.sortable) {
      return;
    }

    if (sortColumn === column.key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      return;
    }

    setSortColumn(column.key);
    setSortOrder("asc");
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column.key) {
      return null;
    }

    return sortOrder === "asc" ? faArrowDownShortWide : faArrowDownWideShort;
  };

  const activePage = externalPagination ? externalPage : page;
  const activeRowsPerPage = externalPagination ? externalRowsPerPage : rowsPerPage;
  const totalRows = externalPagination ? externalTotalRows : filteredRows.length;
  const totalPages = externalPagination
    ? Math.max(1, externalTotalPages)
    : Math.max(1, Math.ceil(totalRows / activeRowsPerPage));
  const startIndex =
    totalRows === 0 ? 0 : (activePage - 1) * activeRowsPerPage + 1;
  const endIndex = Math.min(activePage * activeRowsPerPage, totalRows);
  const paginatedRows = externalPagination
    ? filteredRows
    : filteredRows.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
      );
  const getIsDisabled = (column, row, index) =>
    typeof column.disabled === "function"
      ? column.disabled(row, index)
      : Boolean(column.disabled);

  const handleRowsPerPageSelect = (value) => {
    const parsed = Number.parseInt(String(value), 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return;
    }

    if (externalPagination) {
      onExternalRowsPerPageChange?.(parsed);
      return;
    }

    setPage(1);
    setRowsPerPage(parsed);
  };

  return (
    <div className="w-full min-w-0">
      <div className={containerClassName}>
        {typeof onSearchChange === "function" && (
          <div className="flex mb-2 gap-4 justify-end">
            <div className="flex items-center">
              <InputField
                icon={faSearch}
                placeholder={searchPlaceholder ?? t("common.search")}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>
        )}

        <table className={`${tableClassName} ${tableMinWidthClassName}`.trim()}>
        <thead>
          <tr className={headerRowClassName}>
            {visibleColumns.map((column) => (
              <th
                key={column.key}
                className={`${getWidthClassName(column, headerClassName)} ${column.headerClassName ?? ""} ${column.sortable ? "cursor-pointer" : ""}`.trim()}
                style={getWidthStyle(column)}
                onClick={() => handleSort(column)}
              >
                {column.header}
                {getSortIcon(column) && (
                  <FontAwesomeIcon
                    icon={getSortIcon(column)}
                    className="w-4 pl-2"
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedRows.length > 0 ? (
            paginatedRows.map((row, index) => (
              <tr
                key={rowKey ? rowKey(row, index) : index}
                className={rowClassName ? rowClassName(row, index) : undefined}
              >
                {visibleColumns.map((column) => {
                  const isDisabled = getIsDisabled(column, row, index);

                  if (column.rawCell) {
                    const rawCellElement = column.render(row, index);

                    return cloneElement(rawCellElement, {
                      key: column.key,
                      disabled: isDisabled,
                      className: `${getWidthClassName(column, cellClassName)} ${rawCellElement.props.className ?? ""}`.trim(),
                      style: {
                        ...(rawCellElement.props.style ?? {}),
                        ...getWidthStyle(column),
                      },
                    });
                  }

                  return (
                    <td
                      key={column.key}
                      className={`${getWidthClassName(column, cellClassName)} ${column.cellClassName ?? ""}`.trim()}
                      style={getWidthStyle(column)}
                    >
                      {column.render(row, index, { disabled: isDisabled })}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={emptyColSpan ?? visibleColumns.length}
                className="px-4 py-10 text-center"
              >
                {emptyContent ? (
                  <div className="empty-state mx-auto max-w-md px-6 py-8 text-sm">
                    {emptyContent}
                  </div>
                ) : null}
              </td>
            </tr>
          )}
        </tbody>
        </table>
      </div>

      {showPagination && totalRows > 0 && (
        <div className="flex gap-4 justify-end items-center text-mute-900 pt-4">
          <p>{t("pagination.rowsPerPage")}</p>
          <div className="w-32">
            <Dropdown
              hideLabel
              options={rowsPerPageOptions.map((option) => String(option))}
              selectedOptions={[String(activeRowsPerPage)]}
              managedSelections
              onSelect={handleRowsPerPageSelect}
            />
          </div>
          <p>{t("pagination.range", { start: startIndex, end: endIndex, total: totalRows })}</p>
          <FontAwesomeIcon
            className={`cursor-pointer ${activePage <= 1 ? "opacity-40 pointer-events-none" : ""}`}
            icon={faChevronLeft}
            onClick={() => {
              if (activePage > 1) {
                if (externalPagination) {
                  onExternalPageChange?.(activePage - 1);
                  return;
                }

                setPage(activePage - 1);
              }
            }}
          />
          <FontAwesomeIcon
            className={`cursor-pointer ${activePage >= totalPages ? "opacity-40 pointer-events-none" : ""}`}
            icon={faChevronRight}
            onClick={() => {
              if (activePage < totalPages) {
                if (externalPagination) {
                  onExternalPageChange?.(activePage + 1);
                  return;
                }

                setPage(activePage + 1);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DataTable;
